import { Controller } from "stimulus";

export default class CarouselController extends Controller {
  static targets = ["container", "overlay", "overlayImage", "indicator"];

  connect() {
    this.index = 0;
    // Stimulus v1 style: target must exist; if missing, this will throw, which is fine in dev.
    this.slides = Array.from(this.containerTarget.children);
    this.dragSlides = null;
    this.dragWidth = 0;
    this.dragOriginalStyles = new Map();
    this.touchMode = this.detectTouchDevice();
    if (this.touchMode) {
      this.registerTouchEvents();
    }
    this.updateSlides();
    this.updateControls();
    this.updateIndicators();
  }

  disconnect() {
    this.unregisterTouchEvents();
  }

  prev(event) {
    this.changeSlide(-1, event);
  }

  next(event) {
    this.changeSlide(1, event);
  }

  changeSlide(step, event = null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const newIndex = this.index + step;
    if (newIndex < 0 || newIndex >= this.slides.length) return;
    this.index = newIndex;
    this.scroll();
  }

  scroll() {
    this.updateSlides();
    this.updateControls();
    this.updateIndicators();
  }

  updateControls() {
    const prev = this.element.querySelector('[data-carousel-prev]');
    const next = this.element.querySelector('[data-carousel-next]');

    if (this.touchMode) {
      if (prev) {
        prev.disabled = true;
        prev.classList.add('hidden');
        prev.style.display = "none";
      }
      if (next) {
        next.disabled = true;
        next.classList.add('hidden');
        next.style.display = "none";
      }
      return;
    }

    const single = this.slides.length <= 1;
    if (prev) {
      prev.classList.remove('hidden');
      const hidePrev = single || this.index <= 0;
      prev.disabled = hidePrev;
      prev.style.display = hidePrev ? "none" : "inline-flex";
    }
    if (next) {
      next.classList.remove('hidden');
      const hideNext = single || this.index >= this.slides.length - 1;
      next.disabled = hideNext;
      next.style.display = hideNext ? "none" : "inline-flex";
    }
  }

  updateSlides() {
    this.preloadAroundIndex(this.index);
    this.slides.forEach((el, i) => {
      const active = i === this.index;
      el.style.display = active ? "block" : "none";
      if (!active) {
        el.style.transition = '';
        el.style.transform = '';
        el.style.marginTop = '';
        el.style.marginBottom = '';
        el.style.position = '';
        el.style.left = '';
        el.style.top = '';
        el.style.width = '';
        el.style.zIndex = '';
      }
    });
  }

  loadSlide(slide) {
    const imgs = slide.querySelectorAll('img[data-carousel-lazy-src]');
    if (!imgs) return;

    imgs.forEach((img) => {
      const src = img.dataset.carouselLazySrc;
      if (!src) return;
      img.src = src;
      img.removeAttribute('data-carousel-lazy-src');
    });
  }

  open(event) {
    const target = event.currentTarget || event.target;
    const container = target.closest('[data-full-url]') || target;
    const fullUrl = container.getAttribute('data-full-url') || container.querySelector('img')?.src;
    if (!fullUrl || !this.hasOverlayTarget || !this.hasOverlayImageTarget) return;
    this.overlayImageTarget.src = fullUrl;
    this.overlayTarget.classList.remove('hidden');
  }

  close() {
    if (!this.hasOverlayTarget || !this.hasOverlayImageTarget) return;
    this.overlayImageTarget.src = '';
    this.overlayTarget.classList.add('hidden');
  }

  updateIndicators() {
    if (!this.hasIndicatorTarget) return;

    this.indicatorTargets.forEach((indicator, i) => {
      const isActive = i === this.index;
      indicator.classList.toggle('opacity-100', isActive);
      indicator.classList.toggle('opacity-40', !isActive);
    });
  }

  detectTouchDevice() {
    if (typeof window === 'undefined') return false;
    if ('ontouchstart' in window) return true;
    const hasTouchPoints = navigator?.maxTouchPoints > 0 || navigator?.msMaxTouchPoints > 0;
    if (hasTouchPoints) return true;
    if (window.matchMedia) {
      return window.matchMedia('(pointer: coarse)').matches;
    }
    return false;
  }

  registerTouchEvents() {
    if (!this.containerTarget) return;
    this.boundTouchStart = this.handleTouchStart.bind(this);
    this.boundTouchMove = this.handleTouchMove.bind(this);
    this.boundTouchEnd = this.handleTouchEnd.bind(this);

    this.containerTarget.addEventListener('touchstart', this.boundTouchStart, { passive: true });
    this.containerTarget.addEventListener('touchmove', this.boundTouchMove, { passive: true });
    this.containerTarget.addEventListener('touchend', this.boundTouchEnd);
  }

  unregisterTouchEvents() {
    if (!this.containerTarget || !this.boundTouchStart) return;
    this.containerTarget.removeEventListener('touchstart', this.boundTouchStart, { passive: true });
    this.containerTarget.removeEventListener('touchmove', this.boundTouchMove, { passive: true });
    this.containerTarget.removeEventListener('touchend', this.boundTouchEnd);
    this.boundTouchStart = null;
    this.boundTouchMove = null;
    this.boundTouchEnd = null;
  }

  handleTouchStart(event) {
    if (event.touches.length > 1) {
      this.touchStartX = null;
      return;
    }
    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchDeltaX = 0;
    this.touchDeltaY = 0;
    this.isDragging = true;
    this.preloadAroundIndex(this.index);
    this.prepareDragSlides();
  }

  handleTouchMove(event) {
    if (this.touchStartX == null || event.touches.length > 1) return;
    const touch = event.touches[0];
    this.touchDeltaX = touch.clientX - this.touchStartX;
    this.touchDeltaY = touch.clientY - this.touchStartY;
    this.positionDragSlides(this.touchDeltaX);
  }

  handleTouchEnd() {
    if (this.touchStartX == null) return;

    const deltaX = this.touchDeltaX || 0;
    const deltaY = this.touchDeltaY || 0;
    this.touchStartX = null;
    this.touchStartY = null;
    this.isDragging = false;

    const horizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40;
    let direction = 0;
    if (horizontalSwipe) {
      if (deltaX > 0 && this.index > 0) {
        direction = -1;
      } else if (deltaX < 0 && this.index < this.slides.length - 1) {
        direction = 1;
      }
    }

    if (direction === 0) {
      this.animateDragToBase().then(() => {
        this.cleanupDragSlides();
      });
      return;
    }

    this.animateDragToDirection(direction).then(() => {
      this.cleanupDragSlides();
      this.index += direction;
      this.scroll();
    });
  }

  preloadAroundIndex(index) {
    if (!this.slides.length) return;
    const targetIndices = [index - 1, index, index + 1];
    targetIndices.forEach((idx) => {
      if (idx < 0 || idx >= this.slides.length) return;
      this.loadSlide(this.slides[idx]);
    });
  }

  prepareDragSlides() {
    this.cleanupDragSlides();
    const activeSlide = this.slides[this.index];
    if (!activeSlide) return;

    this.dragSlides = [];
    this.dragWidth = this.containerTarget.clientWidth || activeSlide.clientWidth;
    let maxHeight = 0;

    [-1, 0, 1].forEach((offset) => {
      const slideIndex = this.index + offset;
      if (slideIndex < 0 || slideIndex >= this.slides.length) return;
      const slide = this.slides[slideIndex];
      const original = {
        position: slide.style.position || '',
        left: slide.style.left || '',
        top: slide.style.top || '',
        width: slide.style.width || '',
        marginTop: slide.style.marginTop || '',
        marginBottom: slide.style.marginBottom || '',
        display: slide.style.display || '',
        zIndex: slide.style.zIndex || '',
        transition: slide.style.transition || '',
        transform: slide.style.transform || '',
      };
      this.dragOriginalStyles.set(slide, original);
      const computed = window.getComputedStyle(slide);
      const marginTop = parseFloat(computed.marginTop) || 0;
      const marginBottom = parseFloat(computed.marginBottom) || 0;
      const totalHeight = slide.offsetHeight + marginTop + marginBottom;
      maxHeight = Math.max(maxHeight, totalHeight);
      slide.style.display = 'block';
      slide.style.position = 'absolute';
      slide.style.left = '0';
      slide.style.top = `${marginTop}px`;
      slide.style.width = '100%';
      slide.style.marginTop = '0';
      slide.style.marginBottom = '0';
      slide.style.zIndex = offset === 0 ? '2' : '1';
      slide.style.transition = 'none';
      this.dragSlides.push({ index: slideIndex, element: slide });
    });

    if (maxHeight > 0) {
      this.containerTarget.style.height = `${maxHeight}px`;
    }

    this.positionDragSlides(0);
  }

  positionDragSlides(delta) {
    if (!this.dragSlides) return;
    const width = this.dragWidth || this.containerTarget.clientWidth || 0;
    this.dragSlides.forEach(({ index, element }) => {
      const base = (index - this.index) * width;
      element.style.transform = `translateX(${base + delta}px)`;
    });
  }

  animateDragToBase() {
    return this.animateDragSlides((index) => (index - this.index) * (this.dragWidth || 0));
  }

  animateDragToDirection(direction) {
    return this.animateDragSlides((index) => (index - this.index - direction) * (this.dragWidth || 0));
  }

  animateDragSlides(targetFn) {
    if (!this.dragSlides) return Promise.resolve();

    return new Promise((resolve) => {
      let remaining = 0;
      const width = this.dragWidth || this.containerTarget.clientWidth || 0;
      this.dragSlides.forEach(({ index, element }) => {
        element.style.transition = 'transform 0.25s ease-out';
        const target = targetFn(index);
        remaining += 1;
        const handle = () => {
          element.removeEventListener('transitionend', handle);
          remaining -= 1;
          if (remaining === 0) {
            resolve();
          }
        };
        element.addEventListener('transitionend', handle);
        element.style.transform = `translateX(${target}px)`;
      });

      if (remaining === 0) {
        resolve();
      }
    });
  }

  cleanupDragSlides() {
    if (!this.dragSlides) return;
    this.dragSlides.forEach(({ element }) => {
      const original = this.dragOriginalStyles.get(element) || {};
      element.style.position = original.position;
      element.style.left = original.left;
      element.style.top = original.top;
      element.style.width = original.width;
      element.style.marginTop = original.marginTop;
      element.style.marginBottom = original.marginBottom;
      element.style.display = original.display;
      element.style.zIndex = original.zIndex;
      element.style.transition = original.transition;
      element.style.transform = original.transform;
    });
    this.dragSlides = null;
    this.dragOriginalStyles.clear();
    this.containerTarget.style.height = '';
    this.scroll();
  }
}
